import { BaseTool } from './base';
import {
  getEnabledElementByIds,
  Enums,
  eventTarget,
} from '@cornerstonejs/core';
import type { Types } from '@cornerstonejs/core';
import type { Point3 } from '@cornerstonejs/core/types';
import { getToolGroup } from '../store/ToolGroupManager';
import setViewportCamera, {
  resetViewportCamera,
} from '../utilities/setViewportCamera';
import * as ToolsEnums from '../enums';
import { vec3, mat4, quat } from 'gl-matrix';
import { vtkOrientationControllerWidget } from '../utilities/vtkjs/OrientationControllerWidget';
import { utilities as cornerstoneUtilities } from '@cornerstonejs/core';

const cs3dLogger = cornerstoneUtilities.logger.toolsLog.getLogger(
  'tools.OrientationControllerTool'
);

/**
 * OrientationControllerTool provides an interactive orientation marker
 * using a rhombicuboctahedron (26-faced polyhedron) with clickable surfaces
 * for intuitive 3D volume reorientation.
 *
 * Features:
 * - 6 main square faces with anatomical labels (L/R, A/P, S/I)
 * - 12 edge squares for diagonal orientations
 * - 8 corner triangles for tri-axial views
 * - Smooth animated camera transitions
 * - Configurable appearance and positioning
 *
 * @public
 * @class OrientationControllerTool
 * @extends BaseTool
 */
const ADD_MARKER_DELAY_MS = 500;
const POSITION_RETRY_DELAY_MS = 1000;

type FaceColors = {
  topBottom: number[];
  frontBack: number[];
  leftRight: number[];
};

type LetterColors = {
  zMinus: number[];
  zPlus: number[];
  yMinus: number[];
  yPlus: number[];
  xMinus: number[];
  xPlus: number[];
};

const FACE_COLOR_SCHEMES: Record<string, FaceColors> = {
  marker: {
    topBottom: [0, 0, 255],
    frontBack: [0, 255, 255],
    leftRight: [255, 255, 0],
  },
  gray: {
    topBottom: [180, 180, 180],
    frontBack: [180, 180, 180],
    leftRight: [180, 180, 180],
  },
  rgy: {
    topBottom: [255, 0, 0],
    frontBack: [0, 255, 0],
    leftRight: [255, 255, 0],
  },
};

const LETTER_COLOR_SCHEMES: Record<string, LetterColors> = {
  mixed: {
    zMinus: [255, 255, 255],
    zPlus: [255, 255, 255],
    yMinus: [255, 255, 255],
    yPlus: [255, 255, 255],
    xMinus: [0, 0, 0],
    xPlus: [0, 0, 0],
  },
  rgy: {
    zMinus: [255, 255, 255],
    zPlus: [255, 255, 255],
    yMinus: [255, 255, 255],
    yPlus: [255, 255, 255],
    xMinus: [0, 0, 0],
    xPlus: [0, 0, 0],
  },
  white: {
    zMinus: [255, 255, 255],
    zPlus: [255, 255, 255],
    yMinus: [255, 255, 255],
    yPlus: [255, 255, 255],
    xMinus: [255, 255, 255],
    xPlus: [255, 255, 255],
  },
  black: {
    zMinus: [0, 0, 0],
    zPlus: [0, 0, 0],
    yMinus: [0, 0, 0],
    yPlus: [0, 0, 0],
    xMinus: [0, 0, 0],
    xPlus: [0, 0, 0],
  },
};

const DEFAULT_FACE_COLOR_SCHEME = 'rgy';
const DEFAULT_LETTER_COLOR_SCHEME = 'mixed';

const ANIMATE_RESET_CAMERA_OPTIONS = {
  resetZoom: false,
  resetPan: true,
  resetToCenter: true,
} as const;

class OrientationControllerTool extends BaseTool {
  static toolName = 'OrientationControllerTool';

  private widget = new vtkOrientationControllerWidget();
  private resizeObservers = new Map<string, ResizeObserver>();
  private cameraHandlers = new Map<string, (evt: CustomEvent) => void>();
  private animationFrameHandles = new Map<string, number>();
  private animationTokens = new Map<string, number>();

  constructor(
    toolProps = {},
    defaultToolProps = {
      supportedInteractionTypes: ['Mouse'],
      configuration: {
        enabled: true,
        opacity: 1.0,
        size: 0.04,
        position: 'bottom-right',
        colorScheme: 'rgy',
        letterColorScheme: 'mixed',
        showEdgeFaces: true,
        showCornerFaces: true,
        keepOrientationUp: true,
        highlightColor: [255, 255, 255],
        edgeColor: [200, 200, 200],
        cornerColor: [150, 150, 150],
        restingAmbient: 1.0,
        hoverAmbient: 1.0,
      },
    }
  ) {
    super(toolProps, defaultToolProps);
  }

  private _getViewportsInfo = () => {
    const viewports = getToolGroup(this.toolGroupId)?.viewportsInfo;
    return viewports || [];
  };

  private getPositionConfig() {
    return {
      position: this.configuration.position || 'bottom-right',
      size: this.configuration.size || 0.04,
    };
  }

  private getFaceColors(): {
    topBottom: number[];
    frontBack: number[];
    leftRight: number[];
  } {
    const colorScheme = this.configuration.colorScheme || 'rgy';

    // If faceColors are explicitly provided, use them (but ensure they have the required properties)
    if (this.configuration.faceColors) {
      const provided = this.configuration.faceColors;
      if (provided.topBottom && provided.frontBack && provided.leftRight) {
        return {
          topBottom: provided.topBottom,
          frontBack: provided.frontBack,
          leftRight: provided.leftRight,
        };
      }
    }

    return (
      FACE_COLOR_SCHEMES[colorScheme] ??
      FACE_COLOR_SCHEMES[DEFAULT_FACE_COLOR_SCHEME]
    );
  }

  private getLetterColors(): {
    zMinus: number[];
    zPlus: number[];
    yMinus: number[];
    yPlus: number[];
    xMinus: number[];
    xPlus: number[];
  } {
    const letterColorScheme = this.configuration.letterColorScheme || 'mixed';
    return (
      LETTER_COLOR_SCHEMES[letterColorScheme] ??
      LETTER_COLOR_SCHEMES[DEFAULT_LETTER_COLOR_SCHEME]
    );
  }

  onSetToolEnabled(): void {
    // Remove any existing markers first to ensure clean recreation
    this.removeMarkers();
    this.addMarkers();

    eventTarget.addEventListener(
      ToolsEnums.Events.TOOLGROUP_VIEWPORT_ADDED,
      this.onViewportAdded
    );
    eventTarget.addEventListener(
      ToolsEnums.Events.TOOLGROUP_VIEWPORT_REMOVED,
      this.onViewportRemoved
    );
  }

  onSetToolDisabled(): void {
    this.removeMarkers();

    eventTarget.removeEventListener(
      ToolsEnums.Events.TOOLGROUP_VIEWPORT_ADDED,
      this.onViewportAdded
    );
    eventTarget.removeEventListener(
      ToolsEnums.Events.TOOLGROUP_VIEWPORT_REMOVED,
      this.onViewportRemoved
    );
  }

  onSetToolConfiguration = (): void => {
    // If tool is enabled, recreate markers with new configuration
    const viewportsInfo = this._getViewportsInfo();
    const hasActiveMarkers = viewportsInfo.some(({ viewportId }) => {
      return this.widget.getActors(viewportId) !== null;
    });

    if (hasActiveMarkers) {
      // Remove existing markers and recreate with new configuration
      this.removeMarkers();
      this.addMarkers();
    }
  };

  private onViewportAdded = (evt: CustomEvent): void => {
    const { viewportId, renderingEngineId, toolGroupId } = evt.detail;

    if (toolGroupId !== this.toolGroupId) {
      return;
    }

    if (this.widget.getActors(viewportId)) {
      return;
    }

    setTimeout(() => {
      this.addMarkerToViewport(viewportId, renderingEngineId);
    }, ADD_MARKER_DELAY_MS);
  };

  private onViewportRemoved = (evt: CustomEvent): void => {
    const { viewportId, renderingEngineId, toolGroupId } = evt.detail;

    if (toolGroupId !== this.toolGroupId) {
      return;
    }

    const enabledElement = getEnabledElementByIds(
      viewportId,
      renderingEngineId
    );

    if (enabledElement) {
      const { viewport } = enabledElement;
      if ((viewport as Types.IViewport).isOrientationChangeable()) {
        this.widget.removeActorsFromViewport(
          viewportId,
          viewport as Types.IVolumeViewport
        );
      }
      const cameraHandler = this.cameraHandlers.get(viewportId);
      if (cameraHandler) {
        viewport.element.removeEventListener(
          Enums.Events.CAMERA_MODIFIED,
          cameraHandler as EventListener
        );
        this.cameraHandlers.delete(viewportId);
      }
    }

    this.widget.cleanup(viewportId);

    const resizeObserver = this.resizeObservers.get(viewportId);
    if (resizeObserver) {
      resizeObserver.disconnect();
      this.resizeObservers.delete(viewportId);
    }
  };

  private removeMarkers(): void {
    const viewportsInfo = this._getViewportsInfo();

    viewportsInfo.forEach(({ viewportId, renderingEngineId }) => {
      const enabledElement = getEnabledElementByIds(
        viewportId,
        renderingEngineId
      );

      if (enabledElement) {
        const { viewport } = enabledElement;
        if ((viewport as Types.IViewport).isOrientationChangeable()) {
          this.widget.removeActorsFromViewport(
            viewportId,
            viewport as Types.IVolumeViewport
          );
        }
        const cameraHandler = this.cameraHandlers.get(viewportId);
        if (cameraHandler) {
          viewport.element.removeEventListener(
            Enums.Events.CAMERA_MODIFIED,
            cameraHandler as EventListener
          );
          this.cameraHandlers.delete(viewportId);
        }
      }
      const resizeObserver = this.resizeObservers.get(viewportId);
      if (resizeObserver) {
        resizeObserver.disconnect();
        this.resizeObservers.delete(viewportId);
      }
    });

    this.widget.cleanup();
    this.resizeObservers.forEach((observer) => observer.disconnect());
    this.resizeObservers.clear();
    this.cameraHandlers.clear();
    this.animationFrameHandles.forEach((handle) =>
      cancelAnimationFrame(handle)
    );
    this.animationFrameHandles.clear();
    this.animationTokens.clear();
  }

  private addMarkers = (): void => {
    const viewportsInfo = this._getViewportsInfo();

    viewportsInfo.forEach(({ viewportId, renderingEngineId }) => {
      const enabledElement = getEnabledElementByIds(
        viewportId,
        renderingEngineId
      );

      if (!enabledElement) {
        return;
      }

      const { viewport } = enabledElement;

      if (!(viewport as Types.IViewport).isOrientationChangeable()) {
        return;
      }

      if (this.widget.getActors(viewportId)) {
        return;
      }

      setTimeout(() => {
        this.addMarkerToViewport(viewportId, renderingEngineId);
      }, ADD_MARKER_DELAY_MS);
    });
  };

  private createAnnotatedRhombActor(): ReturnType<
    vtkOrientationControllerWidget['createActors']
  > {
    const faceColors = this.getFaceColors();
    const letterColors = this.getLetterColors();

    return this.widget.createActors({
      faceColors,
      letterColors,
      opacity: this.configuration.opacity ?? 1.0,
      showEdgeFaces: this.configuration.showEdgeFaces !== false,
      showCornerFaces: this.configuration.showCornerFaces !== false,
      highlightColor: this.configuration.highlightColor ?? [255, 255, 255],
      edgeColor: this.configuration.edgeColor ?? [200, 200, 200],
      cornerColor: this.configuration.cornerColor ?? [150, 150, 150],
      restingAmbient: this.configuration.restingAmbient ?? 1.0,
      hoverAmbient: this.configuration.hoverAmbient ?? 1.0,
    });
  }

  private addMarkerToViewport(
    viewportId: string,
    renderingEngineId: string
  ): void {
    if (this.widget.getActors(viewportId)) {
      return;
    }

    const enabledElement = getEnabledElementByIds(
      viewportId,
      renderingEngineId
    );

    if (!enabledElement) {
      cs3dLogger.warn('OrientationControllerTool: No enabled element found');
      return;
    }

    const { viewport } = enabledElement;

    if (!(viewport as Types.IViewport).isOrientationChangeable()) {
      cs3dLogger.warn(
        'OrientationControllerTool: Viewport does not support orientation changes'
      );
      return;
    }

    const element = viewport.element;
    const volumeViewport = viewport as Types.IVolumeViewport;

    const actors = this.createAnnotatedRhombActor();

    this.widget.addActorsToViewport(viewportId, volumeViewport, actors);

    const positioned = this.widget.positionActors(
      volumeViewport,
      actors,
      this.getPositionConfig()
    );

    if (!positioned) {
      cs3dLogger.warn(
        'OrientationControllerTool: Initial positioning failed, retrying...'
      );
      setTimeout(() => {
        const repositioned = this.widget.positionActors(
          volumeViewport,
          actors,
          this.getPositionConfig()
        );
        if (repositioned) {
          viewport.render();
        } else {
          cs3dLogger.error(
            'OrientationControllerTool: Retry positioning also failed'
          );
        }
      }, POSITION_RETRY_DELAY_MS);
    } else {
      viewport.render();
    }

    this.widget.syncOverlayViewport(viewportId, volumeViewport);
    this.widget.setupPicker(viewportId, actors);

    this.widget.setupMouseHandlers(
      viewportId,
      element,
      volumeViewport,
      actors,
      {
        onFacePicked: (result) => {
          const orientation = this.widget.getOrientationForFace(result.cellId);
          if (orientation) {
            this.animateCameraToOrientation(
              volumeViewport,
              orientation.viewPlaneNormal,
              orientation.viewUp
            );
          }
        },
        onFaceHover: (result) => {
          if (result) {
            this.widget.highlightFace(
              result.pickedActor,
              result.cellId,
              volumeViewport,
              result.actorIndex === 0
            );
          } else {
            this.widget.clearHighlight();
          }
        },
      }
    );

    const resizeObserver = new ResizeObserver(() => {
      this.updateMarkerPosition(viewportId, renderingEngineId);
    });
    resizeObserver.observe(element);
    this.resizeObservers.set(viewportId, resizeObserver);

    const cameraHandler = (evt: CustomEvent) => {
      const detail = evt.detail as { viewportId: string };
      if (detail.viewportId === viewportId) {
        this.onCameraModified(evt);
      }
    };
    element.addEventListener(
      Enums.Events.CAMERA_MODIFIED,
      cameraHandler as EventListener
    );
    this.cameraHandlers.set(viewportId, cameraHandler);
  }

  private onCameraModified = (evt: CustomEvent): void => {
    const { viewportId } = evt.detail as { viewportId: string };
    if (!viewportId) {
      return;
    }

    const actors = this.widget.getActors(viewportId);

    if (!actors) {
      return;
    }

    const viewportsInfo = this._getViewportsInfo();
    const viewportInfo = viewportsInfo.find(
      (vp) => vp.viewportId === viewportId
    );

    if (!viewportInfo) {
      return;
    }

    const enabledElement = getEnabledElementByIds(
      viewportId,
      viewportInfo.renderingEngineId
    );

    if (!enabledElement) {
      return;
    }

    const { viewport } = enabledElement;
    if (!(viewport as Types.IViewport).isOrientationChangeable()) {
      return;
    }

    const volumeViewport = viewport as Types.IVolumeViewport;
    this.widget.positionActors(
      volumeViewport,
      actors,
      this.getPositionConfig()
    );
    this.widget.syncOverlayViewport(viewportId, volumeViewport);
    viewport.render();
  };

  private updateMarkerPosition(
    viewportId: string,
    renderingEngineId: string
  ): void {
    const enabledElement = getEnabledElementByIds(
      viewportId,
      renderingEngineId
    );

    if (!enabledElement) {
      return;
    }

    const actors = this.widget.getActors(viewportId);

    if (!actors) {
      return;
    }

    const { viewport } = enabledElement;
    if (!(viewport as Types.IViewport).isOrientationChangeable()) {
      return;
    }

    this.widget.positionActors(
      viewport as Types.IVolumeViewport,
      actors,
      this.getPositionConfig()
    );
    this.widget.syncOverlayViewport(
      viewportId,
      viewport as Types.IVolumeViewport
    );
    viewport.render();
  }

  private animateCameraToOrientation(
    viewport: Types.IVolumeViewport,
    targetViewPlaneNormal: number[],
    targetViewUp: number[]
  ): void {
    const viewportId = viewport.id;
    const existingHandle = this.animationFrameHandles.get(viewportId);
    if (existingHandle !== undefined) {
      cancelAnimationFrame(existingHandle);
      this.animationFrameHandles.delete(viewportId);
    }
    const nextToken = (this.animationTokens.get(viewportId) ?? 0) + 1;
    this.animationTokens.set(viewportId, nextToken);

    const keepOrientationUp = this.configuration.keepOrientationUp !== false; // Default to true

    // Get the VTK camera from the renderer
    const renderer = viewport.getRenderer();
    const camera = renderer.getActiveCamera();
    const directionOfProjection = camera.getDirectionOfProjection();
    const startViewUpArray = camera.getViewUp();

    const startForward = vec3.fromValues(
      -directionOfProjection[0],
      -directionOfProjection[1],
      -directionOfProjection[2]
    );
    const startUp = vec3.fromValues(
      startViewUpArray[0],
      startViewUpArray[1],
      startViewUpArray[2]
    );
    const startRight = vec3.create();
    vec3.cross(startRight, startUp, startForward);
    vec3.normalize(startRight, startRight);

    // prettier-ignore
    const startMatrix = mat4.fromValues(
      startRight[0], startRight[1], startRight[2], 0,
      startUp[0], startUp[1], startUp[2], 0,
      startForward[0], startForward[1], startForward[2], 0,
      0, 0, 0, 1
    );

    const targetForward = vec3.normalize(
      vec3.create(),
      targetViewPlaneNormal as vec3
    );

    let targetUp: vec3;
    if (keepOrientationUp) {
      targetUp = vec3.fromValues(
        targetViewUp[0],
        targetViewUp[1],
        targetViewUp[2]
      );
    } else {
      const currentFwd = vec3.normalize(vec3.create(), startForward);

      const rotQuat = quat.create();
      quat.rotationTo(rotQuat, currentFwd, targetForward);

      targetUp = vec3.create();
      vec3.transformQuat(targetUp, startUp, rotQuat);
      vec3.normalize(targetUp, targetUp);
    }

    // Keep an orthonormal target camera basis so quaternion interpolation
    // matches the final camera vectors and avoids end-of-animation snapping.
    const upDotForward = vec3.dot(targetUp, targetForward);
    vec3.scaleAndAdd(targetUp, targetUp, targetForward, -upDotForward);
    if (vec3.length(targetUp) < 0.0001) {
      targetUp = vec3.clone(startUp);
      const fallbackDot = vec3.dot(targetUp, targetForward);
      vec3.scaleAndAdd(targetUp, targetUp, targetForward, -fallbackDot);
    }
    vec3.normalize(targetUp, targetUp);

    const targetRight = vec3.create();
    vec3.cross(targetRight, targetUp, targetForward);
    vec3.normalize(targetRight, targetRight);
    vec3.cross(targetUp, targetForward, targetRight);
    vec3.normalize(targetUp, targetUp);

    // prettier-ignore
    const targetMatrix = mat4.fromValues(
      targetRight[0], targetRight[1], targetRight[2], 0,
      targetUp[0], targetUp[1], targetUp[2], 0,
      targetForward[0], targetForward[1], targetForward[2], 0,
      0, 0, 0, 1
    );

    const startQuat = mat4.getRotation(quat.create(), startMatrix);
    const targetQuat = mat4.getRotation(quat.create(), targetMatrix);

    let dotProduct = quat.dot(startQuat, targetQuat);
    if (dotProduct < 0) {
      quat.scale(targetQuat, targetQuat, -1);
      dotProduct = -dotProduct;
    }

    const threshold = 0.99996;
    if (dotProduct > threshold) {
      return;
    }

    const duration = 150;
    const animationStart = performance.now();

    const finalNormal: Point3 = [
      targetForward[0],
      targetForward[1],
      targetForward[2],
    ];
    const finalUp: Point3 = [targetUp[0], targetUp[1], targetUp[2]];

    const animate = (now: number) => {
      if (this.animationTokens.get(viewportId) !== nextToken) {
        return;
      }
      const elapsed = now - animationStart;
      const t = Math.min(1, elapsed / duration);
      const isLastStep = t >= 1;
      const easedT = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

      const interpolatedQuat = quat.create();
      quat.slerp(interpolatedQuat, startQuat, targetQuat, easedT);

      const interpolatedMatrix = mat4.create();
      mat4.fromQuat(interpolatedMatrix, interpolatedQuat);

      const interpolatedForward = interpolatedMatrix.slice(8, 11) as Point3;
      const interpolatedUp = interpolatedMatrix.slice(4, 7) as Point3;

      setViewportCamera(viewport, {
        viewPlaneNormal: isLastStep ? finalNormal : interpolatedForward,
        viewUp: isLastStep ? finalUp : interpolatedUp,
      });
      resetViewportCamera(viewport, ANIMATE_RESET_CAMERA_OPTIONS);

      viewport.render();

      if (!isLastStep) {
        const handle = requestAnimationFrame(animate);
        this.animationFrameHandles.set(viewportId, handle);
      } else {
        this.animationFrameHandles.delete(viewportId);
      }
    };

    const handle = requestAnimationFrame(animate);
    this.animationFrameHandles.set(viewportId, handle);
  }
}

export default OrientationControllerTool;
